<template>
    <v-container fluid>
        <v-alert v-if="locationsError" type="error" dense>
            No se pudieron cargar los almacenes. Por favor, recarga la página o contacta al administrador.
        </v-alert>
        <v-alert v-if="!locationsError && locations.length === 0 && !loadingLocations" type="warning" dense>
            No hay almacenes configurados. No se pueden mostrar productos.
        </v-alert>

        <v-card :disabled="locationsError || locations.length === 0">
            <v-card-title>
                Gestión de Productos
                <v-spacer></v-spacer>
                <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn color="secondary" dark class="mr-2" v-bind="attrs" v-on="on">
                            <v-icon left>mdi-file-excel</v-icon> Importar
                        </v-btn>
                    </template>
                    <v-list>
                        <v-list-item @click="downloadSalesReport">
                            <v-list-item-icon>
                                <v-icon>mdi-file-chart</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title>Descargar Reporte de Venta</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="downloadTemplate">
                            <v-list-item-icon>
                                <v-icon>mdi-download</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title>Descargar Plantilla</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="$refs.fileUpload.click()">
                            <v-list-item-icon>
                                <v-icon>mdi-upload</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title>Cargar Excel</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
                <input type="file" ref="fileUpload" class="d-none" accept=".xlsx, .xls" @change="onFileSelected">

                <v-btn color="primary" dark @click="$router.push({ name: 'AdminProductCreate' })">
                    <v-icon left>mdi-plus</v-icon> Añadir Producto
                </v-btn>
            </v-card-title>
            
            <v-card-text>
                <v-row dense>
                     <v-col cols="12" md="4">
                         <v-select
                            v-model="selectedLocationId"
                            :items="locations"
                            item-text="NAME"
                            item-value="ID"
                            label="Almacén / Sucursal"
                            placeholder="Selecciona un almacén"
                            :disabled="loadingLocations" 
                            hide-details
                            outlined
                            dense
                         ></v-select>
                     </v-col>
                     <v-col cols="12" md="4">
                         <v-autocomplete
                           v-model="categorySearchName" 
                           :items="categories"
                           item-text="NAME"
                           item-value="NAME" 
                           label="Categoría"
                           placeholder="Filtrar por categoría"
                           clearable
                           hide-details
                           outlined
                           dense
                           :return-object="false" 
                         ></v-autocomplete>
                     </v-col>
                     <v-col cols="12" md="4">
                        <v-text-field
                            v-model="serverSearch" 
                            append-icon="mdi-magnify"
                            label="Buscar producto por nombre..."
                            single-line
                            hide-details
                            outlined
                            dense
                            clearable 
                        ></v-text-field>
                    </v-col>
                </v-row>
                
                <v-data-table
                  :headers="headers"
                  :items="products"
                  :server-items-length="totalItems" 
                  :options.sync="options" 
                  :loading="loading || loadingLocations"
                  loading-text="Cargando datos..."
                  no-data-text="No se encontraron productos para los filtros seleccionados"
                  no-results-text="No se encontraron productos que coincidan con la búsqueda"
                  class="elevation-1 mt-4" 
                  :footer-props="{ 
                    'items-per-page-options': [5, 10, 25, 50] 
                  }"
                >
                 <!-- eslint-disable-next-line vue/valid-v-slot -->
                  <template v-slot:item.image="{ item }">
                      <v-avatar size="36" tile>
                          <v-img v-if="item.IMAGE" :src="item.IMAGE"></v-img>
                          <v-icon v-else size="20" color="grey">mdi-image-off</v-icon>
                      </v-avatar>
                  </template>
                 <!-- eslint-disable-next-line vue/valid-v-slot -->
                  <template v-slot:item.price="{ item }">
                       <div>Venta: ${{ item.PRICESELL ? Number(item.PRICESELL).toFixed(2) : '0.00' }}</div>
                       <div class="caption grey--text">Compra: ${{ item.PRICEBUY ? Number(item.PRICEBUY).toFixed(2) : '0.00' }}</div>
                  </template>
                 <!-- eslint-disable-next-line vue/valid-v-slot -->
                  <template v-slot:item.stock="{ item }">
                      <v-chip :color="getStockColor(item.stock)" dark small>
                          {{ item.stock }}
                      </v-chip>
                  </template>
                 <!-- eslint-disable-next-line vue/valid-v-slot -->
                  <template v-slot:item.actions="{ item }">
                      <v-icon small class="mr-2" @click="$router.push({ name: 'AdminProductEdit', params: { id: item.ID } })">
                          mdi-pencil
                      </v-icon>
                      <v-icon small color="red" @click="deleteProduct(item)">
                          mdi-delete
                      </v-icon>
                  </template>
                </v-data-table>
            </v-card-text>
        </v-card>
    
        <!-- Diálogo Eliminar -->
         <v-dialog v-model="showDeleteDialog" persistent max-width="400px">
            <v-card>
                <v-card-title class="text-h5">Confirmar Eliminación</v-card-title>
                <v-card-text>¿Estás seguro de que quieres eliminar el producto "{{ itemToDelete ? itemToDelete.NAME : '' }}"? Esta acción no se puede deshacer.</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="grey darken-1" text @click="closeDeleteDialog">Cancelar</v-btn>
                    <v-btn color="red darken-1" text @click="confirmDelete">Eliminar</v-btn>
                    <v-spacer></v-spacer>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Diálogo Resultados Importación -->
        <v-dialog v-model="importResultDialog" max-width="600px">
            <v-card>
                <v-card-title :class="importError ? 'error--text' : 'success--text'">
                    {{ importTitle }}
                </v-card-title>
                <v-card-text>
                    <p>{{ importMessage }}</p>
                    <v-expansion-panels v-if="importDetails.length > 0">
                        <v-expansion-panel>
                            <v-expansion-panel-header>Ver detalles</v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <ul>
                                    <li v-for="(detail, index) in importDetails" :key="index" class="caption red--text">
                                        {{ detail }}
                                    </li>
                                </ul>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="importResultDialog = false">Cerrar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Overlay cargando importación -->
        <v-overlay :value="importing">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
            <div class="mt-2 white--text">Importando productos...</div>
        </v-overlay>

    </v-container>
</template>

<script>
import * as productService from '@/services/productService';
import * as locationService from '@/services/locationService'; 
import * as categoryService from '@/services/categoryService'; 

export default {
  name: 'ProductAdminView',
  data() {
    return {
      serverSearch: '', 
      debouncedSearch: null, 

      loading: false, 
      loadingLocations: true, 
      locationsError: false, 
      showDeleteDialog: false,
      
      // Importación
      importing: false,
      importResultDialog: false,
      importTitle: '',
      importMessage: '',
      importDetails: [],
      importError: false,

      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      },
      totalItems: 0, 

      itemToDelete: null,
      
      headers: [
        { text: 'Imagen', value: 'image', sortable: false, width: '72px' },
        { text: 'Ref (SKU)', value: 'REFERENCE', sortable: true },
        { text: 'Código', value: 'CODE', sortable: true },
        { text: 'Nombre', align: 'start', value: 'NAME', sortable: true }, 
        { text: 'Categoría', value: 'category.NAME', sortable: false }, 
        { text: 'Precios', value: 'price', sortable: false }, 
        { text: 'Stock', value: 'stock', sortable: true }, 
        { text: 'Acciones', value: 'actions', sortable: false, align: 'end' },
      ],
      products: [], 
      locations: [], 
      selectedLocationId: null, 
      categories: [], 
      categorySearchName: null, 
    };
  },
  watch: {
    options: {
      handler() {
        if (this.selectedLocationId) {
            this.fetchProducts();
        }
      },
      deep: true,
    },
    serverSearch() {
      clearTimeout(this.debouncedSearch);
      this.debouncedSearch = setTimeout(() => { 
          if (this.selectedLocationId) { 
              this.options.page = 1; 
              this.fetchProducts();
          } 
      }, 500); 
    },
    selectedLocationId(newVal) {
        if (newVal !== null) {
            this.options.page = 1; 
            this.fetchProducts();
        } else {
             this.products = [];
             this.totalItems = 0;
        }
    },
    categorySearchName() {
        if (this.selectedLocationId) { 
            this.options.page = 1;
            this.fetchProducts();
        }
    }
  },
  mounted() {
      this.fetchLocations(); 
      this.fetchCategories(); 
  },
  methods: {
    async fetchLocations() {
        this.loadingLocations = true;
        this.locationsError = false;
        try {
            const fetchedLocations = await locationService.fetchLocations();
            this.locations = fetchedLocations;
            if (this.locations && this.locations.length > 0) {
                this.selectedLocationId = this.locations[0].ID; 
            } else {
                 console.warn("No se encontraron ubicaciones.");
            }
        } catch (error) {
             this.locationsError = true;
             this.$emit('show-alert', { message: 'Error al cargar almacenes.', type: 'error' });
        } finally {
            this.loadingLocations = false;
        }
    },
    async fetchCategories() {
        try {
            this.categories = await categoryService.fetchCategories();
        } catch (error) {
             this.$emit('show-alert', { message: 'Error al cargar categorías.', type: 'error' });
        }
    },
    async fetchProducts() {
      if (!this.selectedLocationId) {
          this.products = []; 
          this.totalItems = 0;
          return; 
      }
      
      this.loading = true;
      const params = {
        ...this.options,
        name: this.serverSearch || undefined, 
        locationId: this.selectedLocationId, 
        categoryName: this.categorySearchName || undefined 
      };
      try {
        const data = await productService.fetchProducts(params);
        this.products = data.products; 
        this.totalItems = data.totalCount;
      } catch (error) {
        if (error.response && error.response.status === 400 && error.response.data?.mensaje?.includes('locationId')){
             this.$emit('show-alert', { message: 'Error: Falta seleccionar un almacén.', type: 'warning' });
        } else {
            this.$emit('show-alert', { message: 'Error al cargar productos.', type: 'error' });
        }
        this.products = [];
        this.totalItems = 0;
      } finally {
        this.loading = false;
      }
    },
    getStockColor(stock) {
       if (stock === undefined || stock === null) return 'grey';
       const numericStock = Number(stock);
      if (numericStock > 20) return 'green';
      if (numericStock > 0) return 'orange';
      return 'red';
    },
    deleteProduct(item) {
       this.itemToDelete = item;
       this.showDeleteDialog = true;
    },
    closeDeleteDialog() {
       this.showDeleteDialog = false;
       this.$nextTick(() => {
         this.itemToDelete = null;
       });
     },
    async confirmDelete() {
      if (!this.itemToDelete) return;
      this.loading = true;
      try {
        await productService.deleteProduct(this.itemToDelete.ID);
        this.$emit('show-alert', { message: 'Producto eliminado con éxito.', type: 'success' });
        this.closeDeleteDialog();
        const currentPageItemCount = this.products.length;
        if (currentPageItemCount === 1 && this.options.page > 1) {
          this.options.page -= 1;
        } else {
            this.fetchProducts(); 
        }
      } catch (error) {
         this.$emit('show-alert', { 
            message: `Error al eliminar producto: ${error.response?.data?.mensaje || error.message}`,
            type: 'error' 
         });
         this.closeDeleteDialog(); 
      } finally {
         this.loading = false;
      }
     },
     
     // Importación Masiva
     async downloadSalesReport() {
         try {
             const blob = await productService.downloadSalesReport();
             const url = window.URL.createObjectURL(new Blob([blob]));
             const link = document.createElement('a');
             link.href = url;
             link.setAttribute('download', `reporte_productos_venta_${new Date().toISOString().slice(0, 10)}.xlsx`);
             document.body.appendChild(link);
             link.click();
             link.remove();
         } catch (error) {
             this.$emit('show-alert', { message: 'Error al descargar el reporte de venta.', type: 'error' });
         }
     },
     
     async downloadTemplate() {
         try {
             const blob = await productService.downloadTemplate();
             const url = window.URL.createObjectURL(new Blob([blob]));
             const link = document.createElement('a');
             link.href = url;
             link.setAttribute('download', 'plantilla_productos.xlsx');
             document.body.appendChild(link);
             link.click();
             link.remove();
         } catch (error) {
             this.$emit('show-alert', { message: 'Error al descargar plantilla', type: 'error' });
         }
     },
     
     async onFileSelected(event) {
         const file = event.target.files[0];
         if (!file) return;
         
         this.importing = true;
         try {
             const result = await productService.bulkImport(file);
             this.importTitle = 'Importación Completada';
             this.importMessage = result.mensaje;
             this.importDetails = result.detalles || [];
             this.importError = false;
             this.importResultDialog = true;
             
             // Recargar productos
             this.fetchProducts();
         } catch (error) {
             this.importTitle = 'Error en Importación';
             this.importMessage = error.response?.data?.mensaje || 'Error al procesar el archivo.';
             this.importDetails = error.response?.data?.errors || [];
             this.importError = true;
             this.importResultDialog = true;
         } finally {
             this.importing = false;
             // Reset input
             event.target.value = '';
         }
     }
  },
};
</script>

<style scoped>
/* Estilos específicos si son necesarios */
</style>
